import type { Node as PMNode, Schema } from '@milkdown/kit/prose/model';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import { $prose } from '@milkdown/kit/utils';

import { parseSizeFromTitle } from './parse';

const IMAGE = 'image';
const IMAGE_BLOCK = 'image-block';
const PARAGRAPH = 'paragraph';

export const PLATFORM_LIFT_INLINE_IMAGES_META = 'platformLiftInlineImages';

const platformLiftInlineImagesKey = new PluginKey('platformLiftInlineImages');

type ImageLikeAttrs = {
  src?: string;
  alt?: string;
  title?: string;
};

function isIgnorableParagraphChild(node: PMNode): boolean {
  if (node.type.name === 'hardbreak') return true;
  if (node.isText && /^\s*$/.test(node.text ?? '')) return true;

  return false;
}

function findTrailingImageIndex(paragraph: PMNode): number {
  for (let i = paragraph.childCount - 1; i >= 0; i--) {
    const child = paragraph.child(i);
    if (child.type.name === IMAGE) return i;
    if (!isIgnorableParagraphChild(child)) return -1;
  }

  return -1;
}

function paragraphIsOnlyImage(paragraph: PMNode): boolean {
  if (paragraph.childCount === 0) return false;
  let sawImage = false;
  for (let i = 0; i < paragraph.childCount; i++) {
    const child = paragraph.child(i);
    if (child.type.name === IMAGE) {
      if (sawImage) return false;
      sawImage = true;
      continue;
    }
    if (!isIgnorableParagraphChild(child)) return false;
  }

  return sawImage;
}

function isInsideTable($pos: { depth: number; node: (d: number) => PMNode }): boolean {
  for (let d = $pos.depth; d > 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === 'table' || name === 'table_cell' || name === 'table_header') return true;
  }

  return false;
}

function toImageBlock(schema: Schema, image: PMNode): PMNode | null {
  const imageBlockType = schema.nodes[IMAGE_BLOCK];
  if (!imageBlockType) return null;

  const attrs = image.attrs as ImageLikeAttrs;
  const { width, height, cleanTitle } = parseSizeFromTitle(attrs.title);
  const caption = attrs.alt || cleanTitle || '';

  return imageBlockType.create({
    src: attrs.src || '',
    caption,
    ratio: 1,
    width,
    height,
  });
}

export type InlineImageLift = {
  from: number;
  to: number;
  nodes: PMNode[];
};

/**
 * Find paragraph-level inline images that should become block images:
 * - paragraph that is only an image (optional whitespace/breaks)
 * - paragraph that ends with an image after other content (split text + image-block)
 *
 * Skips table cells (must stay inline for GFM).
 */
export function collectInlineImageLifts(doc: PMNode, schema: Schema): InlineImageLift[] {
  if (!schema.nodes[IMAGE_BLOCK] || !schema.nodes[IMAGE]) return [];

  const lifts: InlineImageLift[] = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== PARAGRAPH) return;
    if (isInsideTable(doc.resolve(pos + 1))) return;

    if (paragraphIsOnlyImage(node)) {
      let image: PMNode | null = null;
      for (let i = 0; i < node.childCount; i++) {
        if (node.child(i).type.name === IMAGE) {
          image = node.child(i);
          break;
        }
      }
      if (!image) return;
      const block = toImageBlock(schema, image);
      if (!block) return;
      lifts.push({ from: pos, to: pos + node.nodeSize, nodes: [block] });

      return false;
    }

    const imageIndex = findTrailingImageIndex(node);
    if (imageIndex <= 0) return;

    const image = node.child(imageIndex);
    const block = toImageBlock(schema, image);
    if (!block) return;

    const before: PMNode[] = [];
    for (let i = 0; i < imageIndex; i++) {
      before.push(node.child(i));
    }
    // Drop trailing hardbreaks/whitespace immediately before the image.
    while (before.length > 0 && isIgnorableParagraphChild(before[before.length - 1]!)) {
      before.pop();
    }

    const replacement: PMNode[] = [];
    if (before.length > 0) {
      replacement.push(node.type.create(node.attrs, before, node.marks));
    }
    replacement.push(block);
    lifts.push({ from: pos, to: pos + node.nodeSize, nodes: replacement });

    return false;
  });

  return lifts;
}

function applyInlineImageLifts(
  tr: import('@milkdown/kit/prose/state').Transaction,
  lifts: InlineImageLift[],
): import('@milkdown/kit/prose/state').Transaction | null {
  if (lifts.length === 0) return null;

  const sorted = [...lifts].sort((a, b) => b.from - a.from);
  for (const { from, to, nodes } of sorted) {
    tr = tr.replaceWith(from, to, nodes);
  }
  if (!tr.docChanged) return null;
  tr.setMeta(PLATFORM_LIFT_INLINE_IMAGES_META, true);

  return tr;
}

/**
 * Promotes eligible inline `image` nodes to resizable `image-block` after collab sync / parse.
 */
export const platformLiftInlineImages = $prose(() => {
  return new Plugin({
    key: platformLiftInlineImagesKey,
    appendTransaction(transactions, _oldState, newState) {
      if (transactions.some((txn) => txn.getMeta(PLATFORM_LIFT_INLINE_IMAGES_META))) {
        return null;
      }
      // Avoid fighting dimension-sync meta transactions; lift on doc changes only.
      if (!transactions.some((txn) => txn.docChanged)) return null;

      const lifts = collectInlineImageLifts(newState.doc, newState.schema);
      if (lifts.length === 0) return null;

      return applyInlineImageLifts(newState.tr, lifts);
    },
  });
});
